import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useOutletContext,
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Box, Stack, Typography, Skeleton, Tooltip, IconButton, Drawer, Fab, useMediaQuery, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MessageSquare, Users, Search, X, ArrowLeft, ChevronDown } from "lucide-react";

import { Card, CardContent, CardHeader } from "../../components/Card";
import { Button } from "../../components/Button";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import { ThreadInterface } from "./ThreadInterface";
import { MembersModal } from "../../components/MembersModal";
import { ClientAccordionItem } from "./ClientAccordionItem";
import { ThreadListView } from "./ThreadListView";
import { CreateThreadModal } from "./CreateThreadModal";
import SuccessPopup from "../../components/SuccessPopup";
import { SearchMessagesModal } from "./SearchMessagesModal";
import { UnifiedModal } from "../../components/UnifiedModal";
import { EmptyStateMessage } from "../../components/EmptyStateMessage";
import apiClient from "../../api-client";
import { GlobalMessageWall } from "./GlobalMessageWall";
import { encodeUrlSafeBase64, decodeUrlSafeBase64 } from "./threadUrlUtils";
import { useTheme as useCustomTheme } from "../../theme/useTheme";
import { UnderlineTabs } from "../Clients/metrics/UnderlineTabs";


// Mock data for user permissions
const getUserPermissions = (userRole) => {
  switch (userRole) {
    case "superadmin":
    case "agency_admin":
    case "consultant":
    case "client":
      return {
        canCreateThreads: true,
        canViewAllThreads: ["superadmin", "agency_admin"].includes(userRole),
        canManageThreads: ["superadmin", "agency_admin"].includes(userRole),
      };
    default:
      return {
        canCreateThreads: false,
        canViewAllThreads: false,
        canManageThreads: false,
      };
  }
};

const SUPERADMIN_ALL_CLIENTS_VALUE = "__all_clients__";

const resolveClientId = (clientValue) => {
  if (!clientValue) return null;

  if (Array.isArray(clientValue)) {
    return resolveClientId(clientValue[0]);
  }

  if (typeof clientValue === "object") {
    return clientValue.id ?? null;
  }

  return clientValue;
};

export function Conversations() {
  const theme = useTheme();
  const { neutrals } = useCustomTheme();
  const { user: currentUser, interceptorReady } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId, threadId } = useParams(); // Obtener clientId y threadId de la URL
  const {
    setPageContext,
    selectedFilter,
    selectedFilterData,
    changeSelectedFilter,
    conversationsRefreshKey,
    filterRefresh,
    clientIds,
    isMultiClientUser, // Flag desde AppLayout: usuario "client" con más de 1 cliente
  } = useOutletContext();

  const [searchParams, setSearchParams] = useSearchParams();

  // Estados locales
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [isMembersLoading, setIsMembersLoading] = useState(false);
  const [threadParticipants, setThreadParticipants] = useState([]);
  const [expandedClientId, setExpandedClientId] = useState(
    () => clientId || location?.state?.expandClientId || null
  );
  const [showCreateThreadModal, setShowCreateThreadModal] = useState(false);
  const [selectedClientForThread, setSelectedClientForThread] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    isError: false,
  });
  const handleShowNotification = useCallback(
    ({ message = "", isError = false } = {}) => {
      if (!message) return;
      setNotification({
        open: true,
        message,
        isError,
      });
    },
    []
  );

  // Show toast AFTER navigation (used for actions that navigate immediately after success).
  useEffect(() => {
    const toast = location?.state?.toast;
    if (!toast || !toast.message) return;

    setNotification({
      open: true,
      message: toast.message,
      isError: !!toast.isError,
    });

    // Clear toast from history state to prevent re-showing on refresh/back.
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: { ...(location.state || {}), toast: undefined },
    });
  }, [location?.key, location?.pathname, location?.search, location?.state, navigate]);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [searchDefaultFilters, setSearchDefaultFilters] = useState({});
  const [lockSearchClient, setLockSearchClient] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    if (currentUser?.role === "client" || clientId || location?.state?.expandClientId) {
      return "clients";
    }
    // Auto-start in "clients" view for agency_admin/consultant with client filter,
    // unless the user manually switched to global (indicated by skipAutoSelect flag)
    if (
      ["agency_admin", "consultant"].includes(currentUser?.role) &&
      selectedFilter &&
      !location?.state?.skipAutoSelect
    ) {
      return "clients";
    }
    return "global";
  });
  const [threadDrawerOpen, setThreadDrawerOpen] = useState(false);
  const [superadminClientFilter, setSuperadminClientFilter] = useState(
    SUPERADMIN_ALL_CLIENTS_VALUE
  );
  const threadOrderStorageKey = useMemo(() => {
    const userId = currentUser?.id ?? "anonymous";
    const filterKey = selectedFilter ?? "all";
    return `conversations_thread_order_${userId}_${filterKey}`;
  }, [currentUser?.id, selectedFilter]);
  const [localThreadOrder, setLocalThreadOrder] = useState(() => {
    try {
      const stored = sessionStorage.getItem(threadOrderStorageKey);
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn("No se pudo leer el orden de hilos", error);
      return [];
    }
  });
  const clientListRef = useRef(null);
  const lastBumpedClientRef = useRef(null);
  const hasHandledReloadPageResetRef = useRef(false);

  const getClientOrderKey = useCallback((client) => {
    const key = client?.main_thread?.id ?? client?.id ?? null;
    return key == null ? null : String(key);
  }, []);

  useEffect(() => {
    try {
      if (localThreadOrder.length > 0) {
        sessionStorage.setItem(
          threadOrderStorageKey,
          JSON.stringify(localThreadOrder)
        );
      } else {
        sessionStorage.removeItem(threadOrderStorageKey);
      }
    } catch (error) {
      console.warn("No se pudo guardar el orden de hilos", error);
    }
  }, [localThreadOrder, threadOrderStorageKey]);

  // Funciones para el modal de búsqueda
  const openSearchModal = useCallback((defaults = {}, { lockClient = false } = {}) => {
    let finalDefaults = { ...defaults };

    // Si es multi-client CON filtro, auto-seleccionar ese cliente en el modal
    if (isMultiClientUser && selectedFilter && !defaults.client) {
      finalDefaults.client = selectedFilter;
    }

    setSearchDefaultFilters(finalDefaults);
    setLockSearchClient(lockClient);
    setIsSearchModalOpen(true);
  }, [isMultiClientUser, selectedFilter]);

  const closeSearchModal = useCallback(() => {
    setIsSearchModalOpen(false);
    setLockSearchClient(false);
  }, []);

  // React Query client para invalidar cachés manualmente cuando sea necesario
  const queryClient = useQueryClient();

  // Get user permissions
  const userPermissions = getUserPermissions(currentUser?.role);
  const isClientRole = currentUser?.role === "client";
  const canUseGlobalView = !!currentUser && !isClientRole;

  // LÓGICA CLAVE: Multi-client user con filtro seleccionado → actuar como single-client
  // Esto significa mostrar panel de threads en lugar de panel de clientes
  const shouldActAsSingleClient = isClientRole && (!isMultiClientUser || !!selectedFilter);

  // Mobile detection for client view
  // Aplica a TODOS los clientes en móvil (single-client y multi-client)
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isMobileClientView = isMobile && isClientRole;
  const isMobileAgencyView = isMobile && !isClientRole;

  // Función para obtener el endpoint de clientes según el rol (optimizado para conversaciones)
  const getClientsEndpoint = () => {
    // Multi-client con filtro → NO cargar lista de clientes (se comporta como single-client)
    if (isMultiClientUser && selectedFilter) return null;

    // Para clientes con múltiples clientes SIN filtro, cargar la lista de sus clientes
    if (isMultiClientUser && clientIds.length > 0) {
      return `clients/threads/?id__in=${clientIds.join(",")}`;
    }

    // Los clientes con un solo cliente no ven lista de clientes
    if (currentUser?.role === "client") return null;

    switch (currentUser?.role) {
      case "superadmin":
        return selectedFilter
          ? `clients/threads/?agency=${selectedFilter}`
          : "clients/threads/";
      case "agency_admin":
        if (selectedFilter) {
          // Para agency_admin con filtro específico, usamos el endpoint estándar
          return `clients/${selectedFilter}/`;
        } else {
          return `clients/threads/`;
        }
      case "consultant":
        if (selectedFilter) {
          // Para consultant con filtro específico, usamos el endpoint estándar
          return `clients/${selectedFilter}/`;
        } else {
          return `clients/threads/`;
        }
      default:
        return null;
    }
  };

  // Función para obtener el endpoint de threads
  const getThreadsEndpoint = () => {
    // Multi-client CON filtro → cargar threads del cliente seleccionado (actuar como single-client)
    if (isMultiClientUser && selectedFilter) {
      return `communication/threads/?client=${selectedFilter}`;
    }

    // Multi-client SIN filtro → no cargar threads (mostrar panel de clientes)
    if (isMultiClientUser) return null;

    // Single-client → cargar threads normalmente
    if (currentUser?.role === "client" && currentUser?.client) {
      const singleClientId = resolveClientId(currentUser.client);
      return singleClientId ? `communication/threads/?client=${singleClientId}` : null;
    }
    return null;
  };


  // Cargar clientes (para roles no-cliente O para multi-client SIN filtro)
  const {
    data: clientsData,
    isLoading: clientsIsLoading,
    isInitialLoading: clientsIsInitialLoading,
  } = getData(
    getClientsEndpoint(),
    interceptorReady && !!currentUser && (currentUser.role !== "client" || (isMultiClientUser && !selectedFilter)),
    [conversationsRefreshKey, filterRefresh, clientIds?.join(","), selectedFilter]
  );

  // Cargar threads (para clientes que actúan como single-client)
  const {
    data: threadsData,
    isLoading: threadsIsLoading,
    isInitialLoading: threadsIsInitialLoading,
  } = getData(
    getThreadsEndpoint(),
    interceptorReady && !!currentUser && currentUser.role === "client" && shouldActAsSingleClient,
    [conversationsRefreshKey, filterRefresh]
  );

  const isClientsLoading =
    !clientsData && (clientsIsLoading || clientsIsInitialLoading);
  const isThreadsLoading =
    !threadsData && (threadsIsLoading || threadsIsInitialLoading);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(threadOrderStorageKey);
      const parsed = stored ? JSON.parse(stored) : [];
      if (Array.isArray(parsed)) {
        setLocalThreadOrder(parsed);
        return;
      }
    } catch (error) {
      console.warn("No se pudo rehidratar el orden de hilos", error);
    }

    setLocalThreadOrder([]);
  }, [threadOrderStorageKey]);

  useEffect(() => {
    if (!clientsData) return;

    let clientsList = [];
    if (Array.isArray(clientsData)) {
      clientsList = clientsData;
    } else if (clientsData?.results) {
      clientsList = clientsData.results;
    } else if (typeof clientsData === "object") {
      clientsList = [clientsData];
    }

    // SIEMPRE usa el orden del backend cuando llegan datos nuevos
    const incomingKeys = clientsList
      .map(getClientOrderKey)
      .filter(Boolean);

    setLocalThreadOrder(incomingKeys);
  }, [clientsData, getClientOrderKey]);

  // Procesar datos de clientes
  const clients = useMemo(() => {
    if (!clientsData) return [];

    let clientsList = [];
    if (Array.isArray(clientsData)) {
      clientsList = clientsData;
    } else if (clientsData?.results) {
      clientsList = clientsData.results;
    } else if (typeof clientsData === "object") {
      clientsList = [clientsData];
    }

    if (localThreadOrder.length === 0) {
      return clientsList;
    }

    const clientMap = new Map(
      clientsList.map((client) => [getClientOrderKey(client), client])
    );
    const orderedClients = [];
    const processed = new Set();

    localThreadOrder.forEach((key) => {
      const client = clientMap.get(key);
      if (client) {
        orderedClients.push(client);
        processed.add(key);
      }
    });

    clientsList.forEach((client) => {
      const key = getClientOrderKey(client);
      if (!processed.has(key)) {
        orderedClients.push(client);
      }
    });

    return orderedClients;
  }, [clientsData, getClientOrderKey, localThreadOrder]);

  useEffect(() => {
    if (currentUser?.role !== "superadmin" && superadminClientFilter !== SUPERADMIN_ALL_CLIENTS_VALUE) {
      setSuperadminClientFilter(SUPERADMIN_ALL_CLIENTS_VALUE);
    }
  }, [currentUser?.role, superadminClientFilter]);

  useEffect(() => {
    if (currentUser?.role !== "superadmin") return;
    if (superadminClientFilter === SUPERADMIN_ALL_CLIENTS_VALUE) return;

    const exists = clients.some(
      (client) => String(client.id) === String(superadminClientFilter)
    );

    if (!exists) {
      setSuperadminClientFilter(SUPERADMIN_ALL_CLIENTS_VALUE);
    }
  }, [clients, currentUser?.role, superadminClientFilter]);

  // Procesar datos de threads (solo para clientes)
  const threads = useMemo(() => {
    if (!threadsData) return [];

    if (Array.isArray(threadsData)) {
      return threadsData;
    } else if (threadsData?.results) {
      return threadsData.results;
    }

    return [];
  }, [threadsData]);

  // Usamos los ID tal cual vienen de la URL para ser compatibles con UUID u otros formatos
  const selectedClientIdParam = clientId || null;
  const selectedThreadIdParam = threadId || null;
  const selectedMessageThreadIdParam = decodeUrlSafeBase64(
    searchParams.get("mt") || searchParams.get("messageThread")
  );
  const selectedMessageThreadTitleParam =
    searchParams.get("mtt") || searchParams.get("messageThreadTitle") || "";

  const clientsForList = useMemo(() => {
    if (!selectedClientIdParam || clients.length === 0) {
      return clients;
    }

    const selectedId = String(selectedClientIdParam);
    const index = clients.findIndex(
      (client) => String(client.id) === selectedId
    );

    if (index <= 0) {
      return clients;
    }

    const selectedClient = clients[index];
    return [
      selectedClient,
      ...clients.slice(0, index),
      ...clients.slice(index + 1),
    ];
  }, [clients, selectedClientIdParam]);

  const selectedClientFromList = useMemo(() => {
    if (!selectedClientIdParam) return null;

    const client = clients.find(
      (item) => String(item.id) === String(selectedClientIdParam)
    );

    if (client) {
      return {
        id: client.id,
        name: client.name,
        agency: client.agency,
        participants: client.participants || [],
      };
    }

    return null;
  }, [selectedClientIdParam, clients]);

  const threadDetailsEndpoint = selectedThreadIdParam
    ? `communication/threads/${selectedThreadIdParam}/`
    : null;

  const { data: selectedThreadDetails } = getData(
    threadDetailsEndpoint,
    interceptorReady && !!currentUser && !!selectedThreadIdParam,
    [selectedThreadIdParam, conversationsRefreshKey]
  );

  const selectedThreadData = useMemo(() => {

    if (selectedThreadDetails) {
      return selectedThreadDetails;
    }

    if (
      currentUser?.role === "client" &&
      selectedThreadIdParam &&
      threads.length > 0
    ) {
      const foundThread = threads.find(
        (thread) => String(thread.id) === String(selectedThreadIdParam)
      );
      return foundThread || null;
    }

    return null;
  }, [
    selectedThreadDetails,
    threads,
    selectedThreadIdParam,
    currentUser?.role,
  ]);

  const activeParticipants = selectedThreadData?.participants || [];

  const clientDataForInterface = useMemo(() => {
    if (selectedThreadData) {
      return {
        id: selectedThreadData.client.id,
        name: selectedThreadData.client.name,
        agency: selectedThreadData.agency,
        participants: activeParticipants,
      };
    }
    return selectedClientFromList;
  }, [selectedThreadData, selectedClientFromList, activeParticipants]);

  // Estado para almacenar los participantes del thread seleccionado para miembros
  const [selectedThreadForMembers, setSelectedThreadForMembers] =
    useState(null);

  const selectedClientData = clientDataForInterface;

  const isMobileThreadOpen = isMobileAgencyView && viewMode === "clients" && !!threadId;

  // El Outlet se remonta al cambiar de ruta: el cliente abierto viaja en el state
  const handleMobileBack = useCallback(() => {
    navigate("/conversations", { state: { expandClientId: clientId } });
  }, [navigate, clientId]);

  const handleViewModeChange = useCallback(
    (newValue) => {
      if (newValue === viewMode) {
        if (isMobileThreadOpen) handleMobileBack();
        return;
      }
      setViewMode(newValue);

      if (newValue === "global") {
        setExpandedClientId(null);
        navigate("/conversations", { state: { skipAutoSelect: true } });
      } else if (newValue === "clients") {
        // Re-enable auto-selection when manually switching back to clients view
        hasAutoSelectedFilterClientRef.current = false;
      }
    },
    [navigate, viewMode, isMobileThreadOpen, handleMobileBack]
  );

  const getHeaderInfo = () => {

    // Si no hay cliente seleccionado, mostrar el título por defecto según el rol
    switch (currentUser.role) {
      case "superadmin":
        return {
          title: "Conversaciones",
          subtitle: "Vista completa de todas las conversaciones con clientes",
        };
      case "agency_admin":
        return {
          title: "Conversaciones",
          subtitle: "Gestiona todas las conversaciones con tus clientes",
        };
      case "consultant":
        return {
          title: "Mis Conversaciones",
          subtitle: "Conversaciones con tus clientes asignados",
        };
      case "client":
        // Multi-client CON filtro: mostrar nombre del cliente en el título
        if (isMultiClientUser && selectedFilter && selectedFilterData) {
          return {
            title: `Comunicación - ${selectedFilterData.name}`,
            subtitle: "Tu hilo de comunicación con el equipo Markezy",
          };
        }
        // Multi-client SIN filtro: header más simple, similar a agency_admin
        if (isMultiClientUser) {
          return {
            title: "Comunicación",
            subtitle: "Gestiona las conversaciones con tus empresas",
          };
        }
        // Móvil (single-client): la búsqueda va en la barra de acciones
        if (isMobileClientView) {
          return {
            title: "Comunicación",
            subtitle: null,
          };
        }
        // En desktop (single-client), mostramos el subtitulo completo con el botón de participantes
        return {
          title: "Comunicación",
          subtitle: (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body1"
                sx={{ color: theme.palette.text.secondary }}
              >
                Tu hilo de comunicación con el equipo Markezy
              </Typography>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsMembersLoading(false);
                  setThreadParticipants(activeParticipants);
                  setSelectedThreadForMembers({
                    name: clientDataForInterface?.name,
                    agency: clientDataForInterface?.agency,
                  });
                  setShowMembersModal(true);
                }}
                sx={{
                  minWidth: "auto",
                  px: 2,
                  color: theme.palette.text.secondary,
                }}
              >
                <Users size={14} style={{ marginRight: 4 }} />
                Ver participantes
              </Button>
            </Box>
          ),
        };
      default:
        return {
          title: "Conversaciones",
          subtitle: "Gestiona las conversaciones",
        };
    }
  };

  // Set page context
  useEffect(() => {
    if (currentUser) {
      const headerInfo = getHeaderInfo();

      // Generar breadcrumb según el rol y el cliente seleccionado
      let breadcrumbItems = null;
      if (currentUser.role === "superadmin") {
        if (selectedClientData) {
          // Hay un cliente seleccionado: Vista Global > Agencia X > Cliente X (con filtro) o Vista Global > Cliente X (sin filtro)
          if (selectedFilter && selectedFilterData) {
            breadcrumbItems = [
              {
                label: "Vista Global",
                onClick: () => {
                  changeSelectedFilter("global");
                  navigate("/conversations");
                },
              },
              {
                label: `Agencia: ${selectedFilterData.name}`,
                onClick: () => navigate("/conversations"),
              },
              { label: `Cliente: ${selectedClientData.name}` },
            ];
          } else {
            breadcrumbItems = [
              {
                label: "Vista Global",
                onClick: () => navigate("/conversations"),
              },
              { label: `Cliente: ${selectedClientData.name}` },
            ];
          }
        } else if (selectedFilter && selectedFilterData) {
          // Solo filtro de agencia sin cliente específico: Vista Global > Agencia X
          breadcrumbItems = [
            {
              label: "Vista Global",
              onClick: () => {
                changeSelectedFilter("global");
                navigate("/conversations");
              },
            },
            { label: `Agencia: ${selectedFilterData.name}` },
          ];
        } else {
          // Sin filtro ni cliente: Vista Global
          breadcrumbItems = [{ label: "Vista Global" }];
        }
      } else if (
        currentUser.role === "agency_admin" ||
        currentUser.role === "consultant"
      ) {
        if (selectedClientData) {
          // Hay un cliente seleccionado: Vista Global > Cliente X
          breadcrumbItems = [
            {
              label: "Vista Global",
              onClick: () => {
                changeSelectedFilter("global");
                navigate("/conversations");
              },
            },
            { label: `Cliente: ${selectedClientData.name}` },
          ];
        } else if (selectedFilter && selectedFilterData) {
          // Filtro de cliente sin cliente específico seleccionado: Vista Global > Cliente X
          breadcrumbItems = [
            {
              label: "Vista Global",
              onClick: () => {
                changeSelectedFilter("global");
                navigate("/conversations");
              },
            },
            { label: `Cliente: ${selectedFilterData.name}` },
          ];
        } else {
          // Sin filtro: Vista Global
          breadcrumbItems = [{ label: "Vista Global" }];
        }
      }

      const searchButton = (
        <Button
          variant="dark"
          size="default"
          onClick={() => openSearchModal({})}
          sx={{
            gap: "8px",
          }}
        >
          <Search size={16} />
          Buscar mensajes
        </Button>
      );

      setPageContext({
        title: headerInfo.title,
        subtitle: headerInfo.subtitle,
        showTitle: true,
        titleRightContent: null,
        rightContent: isMobileClientView ? null : searchButton, // No mostrar botón separado en móvil cliente
        breadcrumbItems,
      });
    }
  }, [
    currentUser,
    setPageContext,
    selectedClientData,
    selectedFilter,
    selectedFilterData,
    openSearchModal,
    neutrals,
    isMobileClientView,
    isMultiClientUser,
    shouldActAsSingleClient,
  ]);


  // Control de vistas:
  // - Multi-client SIN filtro → panel de clientes
  // - Multi-client CON filtro → panel de threads (como single-client)
  // - Single-client → panel de threads
  const showGlobalView = canUseGlobalView && viewMode === "global" && !isMultiClientUser;
  const showClientListPanel = (!isClientRole || (isMultiClientUser && !selectedFilter)) && viewMode === "clients";
  const showThreadInterface = isClientRole || viewMode === "clients";
  const gridTemplateColumns =
    !isMobile && (isClientRole || viewMode === "clients") ? "1.5fr 3.5fr" : "minmax(0, 1fr)";
  const viewTabs = canUseGlobalView
    ? [
        { value: "global", label: "Muro general" },
        {
          value: "clients",
          label:
            ["agency_admin", "consultant"].includes(currentUser?.role) && selectedFilter
              ? "Por conversaciones"
              : "Por clientes",
        },
      ]
    : null;
  // La barra de tabs (~46px) + el gap (16px) restan altura a los paneles.
  const panelsHeight = viewTabs ? "calc(100vh - 227px)" : "calc(100vh - 165px)";
  const listPanelHeight = isMobile ? "auto" : panelsHeight;
  const isSuperadminOnGlobalView =
    currentUser?.role === "superadmin" && showGlobalView;

  const superadminClientOptions = useMemo(() => {
    if (!isSuperadminOnGlobalView) return [];

    const mappedClients = clients.map((client) => {
      const rawAgencyName =
        typeof client.agency_name === "string" ? client.agency_name.trim() : "";
      const fallbackAgencyName =
        typeof client.agency?.name === "string" ? client.agency.name.trim() : "";
      const agencyName = rawAgencyName || fallbackAgencyName || "";
      const label = agencyName ? `${client.name} · ${agencyName}` : client.name;

      return {
        value: String(client.id),
        label,
      };
    });

    return [
      { value: SUPERADMIN_ALL_CLIENTS_VALUE, label: "Todos los clientes" },
      ...mappedClients,
    ];
  }, [clients, isSuperadminOnGlobalView]);

  const superadminSelectorValue = superadminClientFilter;

  const handleSuperadminClientSelect = useCallback((value) => {
    setSuperadminClientFilter(value);
  }, []);

  const globalWallSelectedClientId = useMemo(() => {
    if (currentUser?.role === "superadmin") {
      return superadminClientFilter === SUPERADMIN_ALL_CLIENTS_VALUE
        ? null
        : superadminClientFilter;
    }

    if (
      (currentUser?.role === "agency_admin" ||
        currentUser?.role === "consultant") &&
      selectedFilter
    ) {
      return selectedFilter;
    }

    return null;
  }, [currentUser?.role, selectedFilter, superadminClientFilter]);

  useEffect(() => {
    if (!currentUser) return;

    if (currentUser.role === "client") {
      setViewMode((prev) => (prev === "clients" ? prev : "clients"));
      return;
    }

    if (clientId) {
      setViewMode((prev) => (prev === "clients" ? prev : "clients"));
    }
  }, [clientId, currentUser?.role]);

  const pageParam = parseInt(searchParams.get("page") ?? "", 10);
  const highlightMessageIdParam = searchParams.get("highlightMessage");
  const highlightPositionParam = parseInt(
    searchParams.get("messagePosition") ?? "",
    10
  );
  const resolvedInitialPage =
    !Number.isNaN(pageParam) && pageParam > 0 ? pageParam : 1;
  const highlightPositionValue = Number.isNaN(highlightPositionParam)
    ? null
    : highlightPositionParam;

  useEffect(() => {
    if (hasHandledReloadPageResetRef.current) return;
    hasHandledReloadPageResetRef.current = true;

    if (resolvedInitialPage <= 1) return;
    if (typeof window === "undefined" || !window.performance) return;

    const navigationEntry = window.performance.getEntriesByType?.("navigation")?.[0];
    const isReloadNavigation =
      navigationEntry?.type === "reload" ||
      window.performance.navigation?.type === 1;

    if (!isReloadNavigation) return;

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("page");
      params.delete("highlightMessage");
      params.delete("messagePosition");
      return params;
    });
  }, [resolvedInitialPage, setSearchParams]);

  const modalClients = useMemo(() => {
    if (currentUser?.role === "client") {
      // Multi-client: mostrar todos sus clientes en el modal de búsqueda
      if (isMultiClientUser) {
        return clients; // Devolver la lista completa de clientes
      }

      // Single-client: mostrar solo su cliente
      if (clientDataForInterface) {
        return [clientDataForInterface];
      }

      if (selectedClientFromList) {
        return [selectedClientFromList];
      }

      if (currentUser?.client) {
        return [
          {
            id: resolveClientId(currentUser.client),
            name: currentUser?.company_name || "Mi empresa",
          },
        ];
      }

      return [];
    }

    return clients;
  }, [
    clients,
    currentUser?.role,
    currentUser?.client,
    currentUser?.company_name,
    clientDataForInterface,
    selectedClientFromList,
    isMultiClientUser,
  ]);

  const handleSearchResultSelect = useCallback(
    async ({ result, page }) => {
      if (!result?.thread_id) {
        setNotification({
          open: true,
          message: "No se pudo abrir el mensaje seleccionado.",
          isError: true,
        });
        return;
      }

      let targetClientId = result.client_id;
      let targetHighlightMessageId = result.id;
      let targetMessagePosition =
        result.message_position ?? result.messagePosition ?? null;

      try {
        const messageResponse = await apiClient.get(
          `communication/messages/${result.id}/`
        );
        const messageData = messageResponse?.data || null;
        const parentMessageId =
          (typeof messageData?.parent === "object"
            ? messageData?.parent?.id
            : messageData?.parent) || null;

        if (parentMessageId) {
          targetHighlightMessageId = parentMessageId;
        }

        if (!targetClientId) {
          const messageThreadClient =
            (typeof messageData?.thread?.client === "object"
              ? messageData?.thread?.client?.id
              : messageData?.thread?.client) || null;
          if (messageThreadClient) {
            targetClientId = messageThreadClient;
          }
        }
      } catch (error) {
        console.warn(
          "No se pudo resolver el contexto completo del mensaje buscado",
          error
        );
      }

      if (!targetClientId) {
        try {
          const threadResponse = await apiClient.get(
            `communication/threads/${result.thread_id}/`
          );
          const threadClient = threadResponse?.data?.client;
          targetClientId =
            (typeof threadClient === "object"
              ? threadClient?.id
              : threadClient) || null;
        } catch (error) {
          console.error("Error resolviendo cliente para el mensaje", error);
          setNotification({
            open: true,
            message:
              "No se pudo obtener la información del cliente para este mensaje.",
            isError: true,
          });
          return;
        }
      }

      if (!targetClientId) {
        setNotification({
          open: true,
          message: "No se encontró el cliente asociado a este mensaje.",
          isError: true,
        });
        return;
      }

      const params = new URLSearchParams();
      const targetPage = Number(page) || 1;
      if (targetPage > 1) {
        params.set("page", String(targetPage));
      }
      params.set("highlightMessage", String(targetHighlightMessageId));
      if (targetMessagePosition != null) {
        params.set("messagePosition", String(targetMessagePosition));
      }

      setExpandedClientId(targetClientId);
      closeSearchModal();
      const query = params.toString();
      navigate(
        query
          ? `/conversations/${targetClientId}/${result.thread_id}?${query}`
          : `/conversations/${targetClientId}/${result.thread_id}`
      );
    },
    [closeSearchModal, navigate, setNotification, setExpandedClientId]
  );

  const handleThreadPageChange = useCallback(
    (newPage) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (newPage > 1) {
          params.set("page", String(newPage));
        } else {
          params.delete("page");
        }
        return params;
      });
    },
    [setSearchParams]
  );

  const handleHighlightConsumed = useCallback(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("highlightMessage");
      params.delete("messagePosition");
      return params;
    });
  }, [setSearchParams]);

  const bumpClientToTop = useCallback(
    ({ clientId: targetClientId, threadId } = {}) => {
      if (viewMode !== "clients") return;

      let key = null;
      if (targetClientId) {
        const clientMatch = clients.find(
          (client) => String(client.id) === String(targetClientId)
        );
        key = clientMatch ? getClientOrderKey(clientMatch) : null;
      }

      if (!key && threadId) {
        key = String(threadId);
      }

      if (!key) return;

      if (targetClientId != null) {
        lastBumpedClientRef.current = String(targetClientId);
      }

      setLocalThreadOrder((prevOrder) => {
        if (prevOrder.length === 0 && clients.length > 0) {
          const seedOrder = clients
            .map(getClientOrderKey)
            .filter(Boolean);
          const filtered = seedOrder.filter((item) => item !== key);
          return [key, ...filtered];
        }

        const filtered = prevOrder.filter((item) => item !== key);
        return [key, ...filtered];
      });

      if (clientListRef.current) {
        clientListRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [viewMode, clients, getClientOrderKey]
  );

  const pendingExpandClientIdRef = useRef(location?.state?.expandClientId || null);
  useEffect(() => {
    if (!pendingExpandClientIdRef.current || clients.length === 0) return;
    bumpClientToTop({ clientId: pendingExpandClientIdRef.current });
    pendingExpandClientIdRef.current = null;
  }, [clients, bumpClientToTop]);

  useEffect(() => {
    if (viewMode !== "clients" || !clientId) {
      lastBumpedClientRef.current = null;
      return;
    }

    if (isClientsLoading) return;

    const clientIdString = String(clientId);
    if (lastBumpedClientRef.current === clientIdString) return;

    bumpClientToTop({ clientId });
  }, [clientId, viewMode, isClientsLoading, bumpClientToTop]);

  const handleClientSearch = useCallback(
    (client) => {
      if (!client) return;
      openSearchModal({ client: client.id }, { lockClient: true });
    },
    [openSearchModal]
  );

  const handleThreadSearch = useCallback(
    (client, thread) => {
      if (!client || !thread) return;
      openSearchModal({ client: client.id, thread: thread.id }, { lockClient: true });
    },
    [openSearchModal]
  );

  const handleGlobalMessageOpen = useCallback(
    (message) => {
      const targetClientId =
        message.client_id ||
        message.client?.id ||
        (typeof message.client === "number" ? message.client : null);
      const targetThreadId =
        message.thread?.id ||
        message.thread_id ||
        (typeof message.thread === "number" ? message.thread : null);

      if (!targetClientId || !targetThreadId) {
        setNotification({
          open: true,
          message:
            "No se pudo abrir este mensaje porque falta información del cliente o hilo.",
          isError: true,
        });
        return;
      }

      // Calcular la página usando la misma lógica que SearchMessagesModal
      const PAGE_SIZE = 10;
      const position = Number(message.message_position) || 0;
      const page = Math.max(1, Math.floor(position / PAGE_SIZE) + 1);

      const params = new URLSearchParams();

      // Añadir la página a los parámetros de la URL si es mayor que 1
      if (page > 1) {
        params.set("page", String(page));
      }

      if (message.id) {
        params.set("highlightMessage", String(message.id));
      }

      const messagePosition =
        message.message_position ?? message.messagePosition ?? null;
      if (messagePosition != null) {
        params.set("messagePosition", String(messagePosition));
      }

      const isSynchronizedThreadMessage =
        message?.is_thread === true ||
        String(message?.is_thread).toLowerCase() === "true";

      if (isSynchronizedThreadMessage && message?.id) {
        params.set("mt", encodeUrlSafeBase64(message.id));

        const synchronizedThreadTitle = (message?.is_thread_title || "").trim();
        if (synchronizedThreadTitle) {
          params.set("mtt", synchronizedThreadTitle);
        }
      }

      const finalUrl =
        params.size > 0
          ? `/conversations/${targetClientId}/${targetThreadId}?${params.toString()}`
          : `/conversations/${targetClientId}/${targetThreadId}`;

      setExpandedClientId(targetClientId);
      navigate(finalUrl);
    },
    [navigate, setNotification]
  );

  // Sistema "No leído" / "Leído" para agency_admin, consultant y client
  // Marca las notificaciones como abiertas (is_opened=True) al navegar a un thread
  const ROLES_WITH_UNREAD = ["agency_admin", "consultant", "client"];
  const isUnreadRole = ROLES_WITH_UNREAD.includes(currentUser?.role);

  const clearThreadBadgeLocally = useCallback(
    ({ clientId, threadId, clearedCount = 0 }) => {
      if (!threadId) return;

      const normalizedClearedCount = Math.max(0, Number(clearedCount) || 0);

      queryClient.setQueriesData(
        {
          predicate: ({ queryKey }) =>
            Array.isArray(queryKey) &&
            queryKey[0] === "getData" &&
            typeof queryKey[1] === "string" &&
            queryKey[1] === `communication/threads/?client=${clientId}`,
        },
        (oldData) => {
          if (!oldData) return oldData;

          const patch = (list) =>
            list.map((thread) => {
              if (String(thread.id) === String(threadId)) {
                return { ...thread, unopened_unread_notifications: 0 };
              }
              return thread;
            });

          if (Array.isArray(oldData)) return patch(oldData);
          if (oldData?.results) return { ...oldData, results: patch(oldData.results) };
          return oldData;
        }
      );

      queryClient.setQueriesData(
        {
          predicate: ({ queryKey }) =>
            Array.isArray(queryKey) &&
            queryKey[0] === "getData" &&
            typeof queryKey[1] === "string" &&
            queryKey[1] === `communication/threads/${threadId}/`,
        },
        (oldData) => {
          if (!oldData || typeof oldData !== "object") return oldData;
          return {
            ...oldData,
            unopened_unread_notifications: 0,
            unopened_notification_ids: [],
          };
        }
      );

      if (!clientId || normalizedClearedCount <= 0) return;

      queryClient.setQueriesData(
        {
          predicate: ({ queryKey }) =>
            Array.isArray(queryKey) &&
            queryKey[0] === "getData" &&
            typeof queryKey[1] === "string" &&
            (queryKey[1].startsWith("clients/threads/") ||
              queryKey[1] === `clients/${clientId}/`),
        },
        (oldData) => {
          if (!oldData) return oldData;

          const patchClient = (client) => {
            if (String(client?.id) !== String(clientId)) return client;

            const currentUnread = Number(client?.unopened_unread_notifications) || 0;
            return {
              ...client,
              unopened_unread_notifications: Math.max(
                0,
                currentUnread - normalizedClearedCount
              ),
            };
          };

          if (Array.isArray(oldData)) return oldData.map(patchClient);
          if (oldData?.results) {
            return { ...oldData, results: oldData.results.map(patchClient) };
          }
          if (typeof oldData === "object" && oldData?.id) {
            return patchClient(oldData);
          }
          return oldData;
        }
      );
    },
    [queryClient]
  );

  const markThreadNotificationsOpened = useCallback(
    async ({
      clientId,
      threadId,
      unreadCount = 0,
      notificationIds = null,
    } = {}) => {
      if (!isUnreadRole || !threadId || (Number(unreadCount) || 0) <= 0) {
        return false;
      }

      let ids = Array.isArray(notificationIds) ? notificationIds.filter(Boolean) : [];

      if (!ids.length) {
        try {
          const response = await apiClient.get(`communication/threads/${threadId}/`);
          ids = Array.isArray(response?.data?.unopened_notification_ids)
            ? response.data.unopened_notification_ids
            : [];
        } catch {
          return false;
        }
      }

      if (!ids.length) return false;

      try {
        const response = await apiClient.post("dashboard/notifications/mark-opened/", { ids });
        if ((response?.data?.updated || 0) > 0) {
          clearThreadBadgeLocally({
            clientId,
            threadId,
            clearedCount: unreadCount,
          });
        }
        return true;
      } catch {
        return false;
      }
    },
    [clearThreadBadgeLocally, isUnreadRole]
  );

  useEffect(() => {
    if (!isUnreadRole) return;
    const ids = selectedThreadDetails?.unopened_notification_ids;
    if (!ids?.length) return;

    markThreadNotificationsOpened({
      clientId: selectedThreadDetails?.client?.id || selectedClientIdParam,
      threadId: selectedThreadDetails?.id || selectedThreadIdParam,
      unreadCount: selectedThreadDetails?.unopened_unread_notifications || 0,
      notificationIds: ids,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThreadDetails?.id, selectedThreadDetails?.unopened_notification_ids?.join(",")]);

  // Función para manejar selección de thread
  const handleThreadSelect = (selectedClientId, selectedThreadId, options = {}) => {

    setExpandedClientId(selectedClientId);
    bumpClientToTop({ clientId: selectedClientId, threadId: selectedThreadId });

    const params = new URLSearchParams();
    const messageThreadId = options?.messageThreadId;
    const messageThreadTitle = (options?.messageThreadTitle || "").trim();

    if (messageThreadId) {
      params.set("mt", encodeUrlSafeBase64(messageThreadId));
      if (messageThreadTitle) {
        params.set("mtt", messageThreadTitle);
      }
    }

    const basePath = `/conversations/${selectedClientId}/${selectedThreadId}`;
    const query = params.toString();
    const newPath = query ? `${basePath}?${query}` : basePath;

    const selectedThread = options?.thread || null;
    if (
      selectedThread &&
      !selectedThread?.is_message_thread &&
      (Number(selectedThread?.unopened_unread_notifications) || 0) > 0
    ) {
      void markThreadNotificationsOpened({
        clientId: selectedClientId,
        threadId: selectedThreadId,
        unreadCount: Number(selectedThread.unopened_unread_notifications) || 0,
      });
    }

    const toast = options?.toast;
    navigate(newPath, toast ? { state: { toast } } : undefined);
  };

  // Función para manejar toggle de accordion - MEJORADO para evitar parpadeo
  const handleClientToggle = (selectedClientId) => {
    // Si el mismo cliente está expandido => colapsar, de lo contrario expanderlo
    const isCurrentlyExpanded =
      String(expandedClientId) === String(selectedClientId);
    const nextExpanded = isCurrentlyExpanded ? null : selectedClientId;
    setExpandedClientId(nextExpanded);
    if (!isCurrentlyExpanded) {
      bumpClientToTop({ clientId: selectedClientId });
    }
  };

  // Función para manejar toggle de accordion en modal móvil - SIN reordenar
  const handleClientToggleInModal = (selectedClientId) => {
    const isCurrentlyExpanded =
      String(expandedClientId) === String(selectedClientId);
    const nextExpanded = isCurrentlyExpanded ? null : selectedClientId;
    setExpandedClientId(nextExpanded);
    // NO llamar a bumpClientToTop para evitar reordenamiento en el modal
  };

  // Callback para cuando ClientAccordionItem carga threads y debe autoseleccionar el general
  const handleAutoSelectGeneralThread = useCallback((clientId, threadId) => {
    // Solo navegar si no hay ya un thread seleccionado del mismo cliente
    if (selectedThreadIdParam && String(selectedClientIdParam) === String(clientId)) {
      return; // Ya hay un thread de este cliente seleccionado
    }

    // Navegar al thread general
    navigate(`/conversations/${clientId}/${threadId}`);
  }, [selectedThreadIdParam, selectedClientIdParam, navigate]);

  // Función para abrir modal de crear thread
  const handleCreateThread = (client) => {
    setSelectedClientForThread(client);
    setShowCreateThreadModal(true);
  };

  // Función para manejar éxito de creación de thread - MEJORADO
  const handleThreadCreated = (newThread) => {
    // Asegurar que el cliente esté expandido
    setExpandedClientId(newThread.client.id);

    // Refrescar datos inmediatamente
    navigate(
      `/conversations/${newThread.client.id}/${newThread.id}?t=${Date.now()}`,
      {
        state: {
          toast: { message: "Hilo temático creado exitosamente", isError: false },
        },
      }
    );

    // Invalidar específicamente la consulta de los threads del cliente implicado
    if (newThread?.client?.id) {
      const endpoint = `communication/threads/?client=${newThread.client.id}`;
      queryClient.invalidateQueries({ queryKey: ["getData", endpoint] });
    }
  };

  const handleThreadTitleUpdate = useCallback(
    async ({ threadId, clientId: targetClientId, title, isMessageThread = false }) => {
      const trimmedTitle = (title || "").trim();

      if (!trimmedTitle) {
        throw new Error("El título no puede estar vacío.");
      }

      if (trimmedTitle.length < 3) {
        throw new Error("El título debe tener al menos 3 caracteres.");
      }

      try {
        const endpoint = isMessageThread
          ? `communication/messages/${threadId}/`
          : `communication/threads/${threadId}/`;
        const payload = isMessageThread
          ? { is_thread_title: trimmedTitle }
          : { title: trimmedTitle };
        const response = await apiClient.patch(
          endpoint,
          payload
        );

        const updatedThread = response.data;
        setNotification({
          open: true,
          message: "Título actualizado correctamente",
          isError: false,
        });

        if (targetClientId) {
          const endpoint = `communication/threads/?client=${targetClientId}`;
          queryClient.invalidateQueries({
            predicate: ({ queryKey }) =>
              Array.isArray(queryKey) &&
              queryKey[0] === "getData" &&
            queryKey[1] === endpoint,
          });
        }

        if (!isMessageThread) {
          const detailEndpoint = `communication/threads/${threadId}/`;
          queryClient.invalidateQueries({
            predicate: ({ queryKey }) =>
              Array.isArray(queryKey) &&
              queryKey[0] === "getData" &&
              queryKey[1] === detailEndpoint,
          });
        }

        return (
          updatedThread ??
          (isMessageThread
            ? { id: threadId, is_thread_title: trimmedTitle }
            : { id: threadId, title: trimmedTitle })
        );
      } catch (error) {
        console.error("Error actualizando título de hilo:", error);
        let errorMessage = "No se pudo actualizar el título.";
        const errorData = error.response?.data;

        if (errorData) {
          if (typeof errorData === "string") {
            errorMessage = errorData;
          } else if (Array.isArray(errorData)) {
            errorMessage = errorData[0];
          } else if (typeof errorData === "object") {
            const firstValue = Object.values(errorData)[0];
            if (Array.isArray(firstValue)) {
              errorMessage = firstValue[0];
            } else if (typeof firstValue === "string") {
              errorMessage = firstValue;
            }
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        setNotification({
          open: true,
          message: errorMessage,
          isError: true,
        });
        throw new Error(errorMessage);
      }
    },
    [queryClient, setNotification]
  );

  // Función para manejar ver miembros
  const handleViewMembers = async (client) => {
    setIsMembersLoading(true);
    setThreadParticipants([]);
    setSelectedThreadForMembers({
      name: client.name,
      agency: client.agency,
    });
    setShowMembersModal(true);

    try {
      // Cargar el thread principal del cliente para obtener todos los participantes
      const mainThreadId = client.main_thread?.id;

      if (mainThreadId) {
        const response = await apiClient.get(
          `communication/threads/${mainThreadId}/`
        );

        const threadData = response.data;
        const participantsToShow = threadData.participants || [];

        setThreadParticipants(participantsToShow);
      } else {
        // Fallback: usar participantes del cliente si no hay main_thread
        const participantsToShow = client?.participants || [];
        setThreadParticipants(participantsToShow);
      }
    } catch (error) {
      console.error("Error cargando participantes del cliente:", error);

      // En caso de error, mostrar lo que tengamos disponible
      const participantsToShow = activeParticipants.length
        ? activeParticipants
        : client?.participants || [];

      setThreadParticipants(participantsToShow);
    } finally {
      setIsMembersLoading(false);
    }
  };

  const previousClientIdRef = useRef(clientId);
  const previousSelectedFilterRef = useRef(selectedFilter);
  // Ref-flag: el efecto de sync URL limpió el filtro → el efecto de cambio de filtro debe ignorar ese cambio
  const filterClearedByUrlRef = useRef(false);
  // Ref para distinguir mount (URL cambió) de update (filtro cambió dentro del mismo mount)
  const isFirstSyncRenderRef = useRef(true);

  // Efecto para multi-cliente y agency_admin/consultant: cuando cambia el filtro, navegar a /conversations
  // para permitir la auto-selección del thread del nuevo cliente
  useEffect(() => {
    const isAgencyOrConsultant = ["agency_admin", "consultant"].includes(currentUser?.role);
    const isMultiClient = isMultiClientUser && currentUser?.role === "client";

    if (!isAgencyOrConsultant && !isMultiClient) return;

    const previousFilter = previousSelectedFilterRef.current;
    previousSelectedFilterRef.current = selectedFilter;

    // Si el filtro cambió (y no es la primera vez que se establece)
    if (previousFilter !== selectedFilter && previousFilter !== undefined) {
      // Si el filtro fue quitado por el efecto de sync URL, no navegar:
      // la URL ya tiene el destino correcto.
      if (filterClearedByUrlRef.current) {
        filterClearedByUrlRef.current = false;
        return;
      }
      // Navegar a /conversations para limpiar clientId y threadId de la URL
      // Esto permitirá que el efecto de auto-selección elija el thread correcto
      navigate("/conversations", { replace: true });
    }
  }, [selectedFilter, isMultiClientUser, currentUser?.role, navigate]);

  // Auto-expandir cliente cuando hay clientId en la URL - MEJORADO
  useEffect(() => {
    if (currentUser?.role === "client") return;

    const previousClientId = previousClientIdRef.current;

    if (previousClientId !== clientId) {
      if (clientId) {
        setExpandedClientId(clientId);
      } else {
        setExpandedClientId(null);
      }

      previousClientIdRef.current = clientId;
    }
  }, [clientId, currentUser?.role]);

  // Sincronización URL ↔ filtro global de AppLayout:
  // key={location.pathname} en el Outlet causa un REMOUNT completo en cada cambio de URL.
  // - En MOUNT (isFirstSyncRenderRef = true): si hay mismatch → la URL manda, quitar filtro.
  // - En UPDATE dentro del mismo mount (= el filtro cambió en state): no limpiar, el filtro manda.
  useEffect(() => {
    // Solo aplica a agency_admin y consultant
    if (!["agency_admin", "consultant"].includes(currentUser?.role)) {
      isFirstSyncRenderRef.current = false;
      return;
    }
    // Solo actuar si hay filtro activo Y clientId en la URL
    if (!selectedFilter || !clientId) {
      isFirstSyncRenderRef.current = false;
      return;
    }
    // Si coinciden, no hacer nada
    if (String(selectedFilter) === String(clientId)) {
      isFirstSyncRenderRef.current = false;
      return;
    }

    // Hay mismatch entre filtro y URL
    if (isFirstSyncRenderRef.current) {
      // MOUNT: el componente se acaba de montar con una URL que no coincide con el filtro.
      // Esto significa que el usuario cambió la URL → la URL manda → quitar filtro.
      isFirstSyncRenderRef.current = false;
      filterClearedByUrlRef.current = true;
      changeSelectedFilter("global");
      return;
    }

    // UPDATE: el filtro cambió dentro del mismo mount (el usuario seleccionó otro filtro).
    // No limpiar → el efecto de cambio de filtro se encargará de navegar a /conversations.
    isFirstSyncRenderRef.current = false;
  }, [clientId, selectedFilter, currentUser?.role, changeSelectedFilter]);

  // Auto-seleccionar cliente del filtro para agency_admin y consultant
  // Cuando tienen un filtro de cliente activo en AppLayout y entran sin clientId en la URL,
  // auto-expandir el cliente filtrado para mejorar la UX
  const hasAutoSelectedFilterClientRef = useRef(false);

  useEffect(() => {
    // Solo aplica a agency_admin y consultant
    if (!["agency_admin", "consultant"].includes(currentUser?.role)) return;
    // Solo si hay un filtro de cliente seleccionado
    if (!selectedFilter) {
      hasAutoSelectedFilterClientRef.current = false;
      return;
    }
    // Solo auto-seleccionar cuando estamos en vista de clientes
    if (viewMode !== "clients") return;
    // No hacer nada si ya hay un clientId en la URL (el usuario ya seleccionó algo)
    if (clientId) return;
    // No repetir si ya autoseleccionamos para este filtro
    if (hasAutoSelectedFilterClientRef.current) return;
    // Esperar a que los clientes estén cargados
    if (isClientsLoading || clients.length === 0) return;

    // Verificar que el cliente del filtro existe en la lista
    const filterClient = clients.find(
      (c) => String(c.id) === String(selectedFilter)
    );
    if (!filterClient) return;

    hasAutoSelectedFilterClientRef.current = true;

    // Expandir el cliente del filtro
    setExpandedClientId(filterClient.id);
    bumpClientToTop({ clientId: filterClient.id });
  }, [currentUser?.role, selectedFilter, clientId, isClientsLoading, clients, bumpClientToTop, viewMode]);

  // Resetear el flag de autoselección cuando cambia el filtro
  useEffect(() => {
    hasAutoSelectedFilterClientRef.current = false;
  }, [selectedFilter]);

  // Auto-seleccionar primer thread para clientes que actúan como single-client
  // Esto incluye: single-client siempre, y multi-client cuando tienen filtro seleccionado
  useEffect(() => {
    // Solo aplicar cuando actúan como single-client
    if (!shouldActAsSingleClient) return;

    if (
      currentUser?.role === "client" &&
      threads.length > 0 &&
      !threadId
    ) {
      // Buscar el thread general primero, sino el primero disponible
      const generalThread = threads.find((t) => t.type === "general");
      const firstThread = generalThread || threads[0];

      // Determinar el ID del cliente según la situación
      let targetClientId;
      if (isMultiClientUser && selectedFilter) {
        // Multi-client con filtro → usar el filtro
        targetClientId = selectedFilter;
      } else {
        // Single-client → usar currentUser.client
        targetClientId = resolveClientId(currentUser.client);
      }

      if (firstThread && targetClientId) {
        navigate(
          `/conversations/${targetClientId}/${firstThread.id}?t=${Date.now()}`,
          { replace: true }
        );
      }
    }
  }, [threads, threadId, currentUser, navigate, isMultiClientUser, shouldActAsSingleClient, selectedFilter]);

  // ==================== MOBILE CLIENT VIEW ====================
  // Vista móvil unificada para todos los clientes (single y multi)
  if (isMobileClientView) {
    // Determinar el cliente actual para el ThreadInterface
    const mobileClientId = isMultiClientUser && selectedFilter
      ? selectedFilter
      : (selectedClientIdParam || resolveClientId(currentUser.client));

    // Título según contexto
    // Sin varios hilos no hay nada que seleccionar (multi-client sin filtro elige empresa)
    const showMobileThreadSelector =
      (isMultiClientUser && !selectedFilter) || (!isThreadsLoading && threads.length > 1);
    const currentMobileThread = threads.find(
      (thread) => !thread.is_message_thread && String(thread.id) === String(selectedThreadIdParam)
    );
    const mobileSelectorLabel =
      selectedMessageThreadTitleParam ||
      (currentMobileThread &&
        (currentMobileThread.title?.trim() ||
          (currentMobileThread.type === "general" ? "General" : null))) ||
      (isMultiClientUser && !selectedFilter
        ? "Seleccionar empresa"
        : "Seleccionar conversación");

    const mobileTitle = isMultiClientUser && selectedFilter && selectedFilterData
      ? `Comunicación - ${selectedFilterData.name}`
      : "Comunicación";

    return (
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          {showMobileThreadSelector && (
            <Button
              variant="outline"
              size="default"
              onClick={() => setThreadDrawerOpen(true)}
              sx={{ flex: 1, minWidth: 0, justifyContent: "flex-start", gap: "8px" }}
            >
              <MessageSquare size={18} style={{ flexShrink: 0 }} />
              <Typography
                variant="body2"
                noWrap
                sx={{ fontWeight: 500, minWidth: 0 }}
              >
                {mobileSelectorLabel}
              </Typography>
              <ChevronDown size={16} style={{ flexShrink: 0, marginLeft: "auto" }} />
            </Button>
          )}

          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            <Button
              variant="dark"
              size="icon"
              aria-label="Buscar mensajes"
              onClick={() => openSearchModal({})}
              sx={{ width: "40px", height: "40px" }}
            >
              <Search size={18} />
            </Button>

            {shouldActAsSingleClient && clientDataForInterface && (
              <Button
                variant="outline"
                size="icon"
                aria-label="Ver participantes"
                onClick={() => handleViewMembers(clientDataForInterface)}
                sx={{ width: "40px", height: "40px" }}
              >
                <Users size={18} />
              </Button>
            )}
          </Box>
        </Box>

        {/* Thread Interface - Full Width */}
        <ThreadInterface
          selectedClientId={mobileClientId}
          mainThreadId={selectedThreadIdParam}
          messageThreadId={selectedMessageThreadIdParam}
          messageThreadTitle={selectedMessageThreadTitleParam}
          currentUser={currentUser}
          clientData={clientDataForInterface}
          participants={activeParticipants}
          conversationsRefreshKey={conversationsRefreshKey}
          initialPage={resolvedInitialPage}
          highlightMessageId={highlightMessageIdParam}
          highlightMessagePosition={highlightPositionValue}
          onPageChange={handleThreadPageChange}
          onHighlightConsumed={handleHighlightConsumed}
          onShowNotification={handleShowNotification}
          isMobileClientView={true}
        />

        {/* Modal-based Conversation Selector */}
        <UnifiedModal
          open={threadDrawerOpen}
          onClose={() => setThreadDrawerOpen(false)}
          title={isMultiClientUser && !selectedFilter ? "Empresas" : "Tus hilos de conversación"}
          maxWidth="sm"
          contentSx={{
            p: 0,
            display: "flex",
            flexDirection: "column",
            maxHeight: "60vh",
            height: "60vh",
            overflow: "hidden",
          }}
        >
          {/* Solo mostrar subtítulo para multiclient sin filtro */}
          {isMultiClientUser && !selectedFilter && (
            <>
              <Box sx={{ p: 2, pb: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Selecciona una empresa
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {/* Multi-client SIN filtro: mostrar lista de empresas */}
            {isMultiClientUser && !selectedFilter ? (
              <Box>
                {isClientsLoading ? (
                  Array.from({ length: 3 }).map((_, index) => (
                    <Box
                      key={`client-skeleton-${index}`}
                      sx={{
                        padding: "16px 20px",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Skeleton variant="text" width="60%" height={18} />
                      <Skeleton variant="text" width="40%" height={16} />
                    </Box>
                  ))
                ) : (
                  clients.map((client) => (
                    <ClientAccordionItem
                      key={client.id}
                      client={client}
                      isExpanded={String(expandedClientId) === String(client.id)}
                      onToggle={() => handleClientToggleInModal(client.id)}
                      selectedThreadId={selectedThreadIdParam}
                      selectedMessageThreadId={selectedMessageThreadIdParam}
                      onThreadSelect={(clientId, threadId, options) => {
                        handleThreadSelect(clientId, threadId, options);
                        // Solo cerrar el modal al seleccionar un thread, no al expandir el cliente
                        setThreadDrawerOpen(false);
                      }}
                      onCreateThread={handleCreateThread}
                      onViewMembers={handleViewMembers}
                      onUpdateThreadTitle={handleThreadTitleUpdate}
                      currentUser={currentUser}
                      refreshKey={conversationsRefreshKey}
                      unreadCount={isUnreadRole ? (client.unopened_unread_notifications || 0) : 0}
                      onShowNotification={({ message = "", isError = false } = {}) => {
                        if (!message) return;
                        setNotification({
                          open: true,
                          message,
                          isError,
                        });
                      }}
                      onSearchClient={null} // Desactivar búsqueda en modal de empresas
                      onSearchThread={null} // Desactivar búsqueda de threads en modal de empresas
                      onAutoSelectGeneralThread={null} // Desactivar autoselección en multiclient
                      shouldAutoSelectGeneral={false} // Desactivar autoselección en multiclient
                    />
                  ))
                )}
                {!isClientsLoading && clients.length === 0 && (
                  <Box sx={{ padding: "64px 32px" }}>
                    <EmptyStateMessage
                      icon={MessageSquare}
                      message="No hay empresas disponibles"
                    />
                  </Box>
                )}
              </Box>
            ) : (
              /* Single-client o Multi-client CON filtro: mostrar lista de threads */
              <ThreadListView
                threads={threads}
                selectedThreadId={selectedThreadIdParam}
                selectedMessageThreadId={selectedMessageThreadIdParam}
                onThreadSelect={(_, tId, options) => {
                  const targetClientId = isMultiClientUser && selectedFilter
                    ? selectedFilter
                    : resolveClientId(currentUser.client);
                  handleThreadSelect(targetClientId, tId, options);
                  setThreadDrawerOpen(false);
                }}
                onCreateThread={() =>
                  handleCreateThread(selectedClientFromList)
                }
                isLoading={isThreadsLoading}
                canCreateThreads={false}
                clientId={mobileClientId}
                onSearchThread={(thread) =>
                  handleThreadSearch(
                    clientDataForInterface ||
                    selectedClientFromList || {
                      id: mobileClientId,
                      name: selectedClientFromList?.name || "",
                    },
                    thread
                  )
                }
                isClientView={true}
                onUpdateThreadTitle={handleThreadTitleUpdate}
              />
            )}
          </Box>
        </UnifiedModal>

        {/* Modal de crear thread */}
        < CreateThreadModal
          open={showCreateThreadModal}
          onClose={() => {
            setShowCreateThreadModal(false);
            setSelectedClientForThread(null);
          }
          }
          client={selectedClientForThread}
          onSuccess={handleThreadCreated}
        />

        {/* Modal de miembros */}
        < MembersModal
          open={showMembersModal}
          onClose={() => {
            setShowMembersModal(false);
            setIsMembersLoading(false);
          }
          }
          isLoading={isMembersLoading}
          participants={threadParticipants}
          clientName={selectedThreadForMembers?.name || ""}
          agencyName={selectedThreadForMembers?.agency?.name || ""}
        />

        < SearchMessagesModal
          open={isSearchModalOpen}
          onClose={closeSearchModal}
          defaultFilters={searchDefaultFilters}
          clients={modalClients}
          currentUser={currentUser}
          onResultSelect={handleSearchResultSelect}
          lockClientSelector={lockSearchClient}
        />

        {/* Popup de notificaciones */}
        < SuccessPopup
          message={notification.message}
          isOpen={notification.open}
          onClose={() =>
            setNotification({ open: false, message: "", isError: false })
          }
          error={notification.isError}
          autoHideDuration={notification.isError ? 4000 : undefined}
        />
      </Box >
    );
  }

  // ==================== DESKTOP VIEW (ORIGINAL) ====================
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {viewTabs && (
        <UnderlineTabs
          tabs={viewTabs}
          value={viewMode}
          onChange={handleViewModeChange}
        />
      )}

      {isMobileThreadOpen && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Button
            variant="outline"
            size="icon"
            aria-label="Volver a clientes"
            onClick={handleMobileBack}
            sx={{ width: "40px", height: "40px", flexShrink: 0 }}
          >
            <ArrowLeft size={18} />
          </Button>
          <Typography variant="body1" noWrap sx={{ fontWeight: 600, minWidth: 0 }}>
            {clientDataForInterface?.name || "Clientes"}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns,
          gap: "24px",
          height: listPanelHeight,
        }}
      >
        {/* Panel izquierdo - Clientes con Accordion (para admin, consultor, superadmin) */}
        {showClientListPanel && !isMobileThreadOpen && (
          <Card
            ref={clientListRef}
            sx={{
              overflowY: isMobile ? "visible" : "auto",
              padding: 0,
              height: "100%",
              maxHeight: listPanelHeight,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.divider,
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: theme.palette.text.secondary,
                },
              },
              scrollbarWidth: "thin",
              scrollbarColor: `${theme.palette.divider} transparent`,
            }}
          >
            <CardHeader
              sx={{
                paddingBottom: "6px",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {isMultiClientUser && !selectedFilter ? "Empresas" : "Clientes"}
              </Typography>
            </CardHeader>
            <CardContent sx={{ padding: 0 }}>
              {isClientsLoading
                ? Array.from({ length: 4 }).map((_, index) => (
                  <Box
                    key={`client-skeleton-${index}`}
                    sx={{
                      padding: "16px 20px",
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Skeleton variant="text" width="60%" height={18} />
                    <Skeleton variant="text" width="40%" height={16} />
                  </Box>
                ))
                : clientsForList.map((client) => {
                  return (
                    <ClientAccordionItem
                      key={client.id}
                      client={client}
                      isExpanded={
                        String(expandedClientId) === String(client.id)
                      }
                      onToggle={() => handleClientToggle(client.id)}
                      selectedThreadId={selectedThreadIdParam}
                      selectedMessageThreadId={selectedMessageThreadIdParam}
                      onThreadSelect={handleThreadSelect}
                      onCreateThread={handleCreateThread}
                      onViewMembers={handleViewMembers}
                      onUpdateThreadTitle={handleThreadTitleUpdate}
                      currentUser={currentUser}
                      refreshKey={conversationsRefreshKey}
                      unreadCount={isUnreadRole ? (client.unopened_unread_notifications || 0) : 0}
                      onShowNotification={({ message = "", isError = false } = {}) => {
                        if (!message) return;
                        setNotification({
                          open: true,
                          message,
                          isError,
                        });
                      }}
                      onSearchClient={handleClientSearch}
                      onSearchThread={(thread) =>
                        handleThreadSearch(client, thread)
                      }
                      onAutoSelectGeneralThread={handleAutoSelectGeneralThread}
                      // En móvil abrir el general taparía la lista
                      shouldAutoSelectGeneral={!isMobile}
                    />
                  );
                })}

              {!isClientsLoading && clients.length === 0 && (
                <Box sx={{ padding: "64px 32px" }}>
                  <EmptyStateMessage
                    icon={MessageSquare}
                    message="No hay clientes disponibles"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Panel izquierdo - Lista de Threads (para clientes que actúan como single-client) */}
        {currentUser?.role === "client" && shouldActAsSingleClient && (
          <Card
            sx={{
              overflowY: "auto",
              padding: 0,
              height: "100%",
              maxHeight: panelsHeight,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.divider,
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: theme.palette.text.secondary,
                },
              },
              scrollbarWidth: "thin",
              scrollbarColor: `${theme.palette.divider} transparent`,
            }}
          >
            <CardHeader
              sx={{
                paddingBottom: "12px",
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    Conversaciones
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Tus hilos de conversación
                  </Typography>
                </Box>
                <Tooltip title="Ver participantes">
                  <IconButton
                    size="small"
                    onClick={() => handleViewMembers(clientDataForInterface)}
                    sx={{
                      color: theme.palette.text.secondary,
                      "&:hover": {
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    <Users size={18} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </CardHeader>
            <CardContent sx={{ padding: 0 }}>
              <ThreadListView
                threads={threads}
                selectedThreadId={selectedThreadIdParam}
                selectedMessageThreadId={selectedMessageThreadIdParam}
                onThreadSelect={(_, tId, options) =>
                  handleThreadSelect(resolveClientId(currentUser.client), tId, options)
                }
                onCreateThread={() =>
                  handleCreateThread(selectedClientFromList)
                }
                isLoading={isThreadsLoading}
                canCreateThreads={currentUser?.role !== "client"}
                clientId={resolveClientId(currentUser.client)}
                onSearchThread={(thread) =>
                  handleThreadSearch(
                    clientDataForInterface ||
                    selectedClientFromList || {
                      id: resolveClientId(currentUser.client),
                      name: selectedClientFromList?.name || "",
                    },
                    thread
                  )
                }
                isClientView={true}
                onUpdateThreadTitle={handleThreadTitleUpdate}
              />
            </CardContent>
          </Card>
        )}

        {/* Vista general de mensajes */}
        {showGlobalView && (
          <Card
            sx={{
              height: "100%",
              maxHeight: panelsHeight,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              maxWidth: "1000px",
            }}
          >
            <GlobalMessageWall
              onMessageOpen={handleGlobalMessageOpen}
              refreshKey={conversationsRefreshKey}
              filterRefresh={filterRefresh}
              currentUser={currentUser}
              interceptorReady={interceptorReady}
              clients={clients}
              enabled={showGlobalView}
              selectedFilter={selectedFilter}
              selectedClientId={globalWallSelectedClientId}
              userRole={currentUser?.role}
              showSuperadminSelector={isSuperadminOnGlobalView}
              superadminSelectorValue={superadminSelectorValue}
              superadminClientOptions={superadminClientOptions}
              onSuperadminClientSelect={handleSuperadminClientSelect}
              conversationFontSize={currentUser?.ui_preferences?.conversation_font_size}
              onShowNotification={handleShowNotification}
            />
          </Card>
        )}

        {/* Panel derecho - Thread de conversación */}
        {showThreadInterface && (!isMobileAgencyView || isMobileThreadOpen) && (
          <ThreadInterface
            selectedClientId={selectedClientIdParam}
            mainThreadId={selectedThreadIdParam}
            messageThreadId={selectedMessageThreadIdParam}
            messageThreadTitle={selectedMessageThreadTitleParam}
            currentUser={currentUser}
            clientData={clientDataForInterface}
            participants={activeParticipants}
            conversationsRefreshKey={conversationsRefreshKey}
            initialPage={resolvedInitialPage}
            highlightMessageId={highlightMessageIdParam}
            highlightMessagePosition={highlightPositionValue}
            onPageChange={handleThreadPageChange}
            onHighlightConsumed={handleHighlightConsumed}
            onShowNotification={handleShowNotification}
            height={panelsHeight}
          />
        )}
      </Box>

      {/* Modal de crear thread */}
      <CreateThreadModal
        open={showCreateThreadModal}
        onClose={() => {
          setShowCreateThreadModal(false);
          setSelectedClientForThread(null);
        }}
        client={selectedClientForThread}
        onSuccess={handleThreadCreated}
      />

      {/* Modal de miembros */}
      <MembersModal
        open={showMembersModal}
        onClose={() => {
          setShowMembersModal(false);
          // Don't clear state here to allow exit animation to show content
          setIsMembersLoading(false);
        }}
        isLoading={isMembersLoading}
        participants={threadParticipants}
        clientName={selectedThreadForMembers?.name || ""}
        agencyName={selectedThreadForMembers?.agency?.name || ""}
      />

      <SearchMessagesModal
        open={isSearchModalOpen}
        onClose={closeSearchModal}
        defaultFilters={searchDefaultFilters}
        clients={modalClients}
        currentUser={currentUser}
        onResultSelect={handleSearchResultSelect}
        lockClientSelector={lockSearchClient}
      />

      {/* Popup de notificaciones */}
      <SuccessPopup
        message={notification.message}
        isOpen={notification.open}
        onClose={() =>
          setNotification({ open: false, message: "", isError: false })
        }
        error={notification.isError}
        autoHideDuration={notification.isError ? 4000 : undefined}
      />
    </Box>
  );
}
